import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { StudioPage, Panel, SectionLabel, Loading, cx } from "./StudioShared";
import { FileDown, FileText, FileJson, FileCode, FileType, Download, Package } from "lucide-react";

const LIST_ENDPOINT = {
  strategist: "/admin/strategists", mission: "/admin/missions",
  workflow: "/admin/workflows", "workflow-snapshot": "/admin/workflows",
  "mission-replay": "/admin/workflow-runs", department: "/admin/departments",
};
const itemLabel = (type, x) =>
  type === "mission-replay" ? `${x.workflow_name} · ${x.status} · ${(x.started_at || "").slice(0, 16).replace("T", " ")}` : x.name;
const FMT_ICON = { pdf: FileType, docx: FileText, markdown: FileCode, html: FileCode, json: FileJson };

export default function ExportCenter() {
  const [cat, setCat] = useState(null);
  const [type, setType] = useState(null);
  const [items, setItems] = useState(null);
  const [objId, setObjId] = useState("");
  const [q, setQ] = useState("");
  const [format, setFormat] = useState("pdf");
  const [profile, setProfile] = useState("executive");
  const [brand, setBrand] = useState("atf");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/export/catalog").then((r) => setCat(r.data)); }, []);

  const meta = useMemo(() => (cat?.objects || []).find((o) => o.type === type), [cat, type]);
  useEffect(() => {
    setItems(null); setObjId(""); setQ("");
    if (type && meta?.needs_id && LIST_ENDPOINT[type]) {
      api.get(LIST_ENDPOINT[type]).then((r) => setItems(r.data)).catch(() => setItems([]));
    }
  }, [type, meta]);

  const grouped = useMemo(() => {
    const g = {};
    (cat?.objects || []).forEach((o) => { (g[o.group] = g[o.group] || []).push(o); });
    return g;
  }, [cat]);

  const canExport = type && (!meta?.needs_id || objId);
  const doExport = async () => {
    if (!canExport) return;
    setBusy(true);
    try {
      const id = meta?.needs_id ? objId : "report";
      const res = await api.get(`/admin/export/${type}/${id}?format=${format}&profile=${profile}&brand=${brand}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const name = m ? m[1] : `${type}-${profile}.${format === "markdown" ? "md" : format}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
      toast.success(`Exported ${name}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (!cat) return <StudioPage title="Export Center" testid="export-center-page"><Loading label="Loading export catalog…" /></StudioPage>;
  const filteredItems = (items || []).filter((x) => !q || itemLabel(type, x).toLowerCase().includes(q.toLowerCase()));

  return (
    <StudioPage title="Export Center" testid="export-center-page"
      question="The AIPOS publishing engine — export any object to PDF, DOCX, Markdown, HTML or JSON with a report profile and brand.">
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Object picker */}
        <Panel className="p-4 lg:col-span-1" data-testid="export-objects">
          <SectionLabel className="mb-3">1 · Choose an object</SectionLabel>
          <div className="space-y-4">
            {Object.entries(grouped).map(([group, objs]) => (
              <div key={group}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{group}</div>
                <div className="space-y-1">
                  {objs.map((o) => (
                    <button key={o.type} onClick={() => setType(o.type)} data-testid={`export-type-${o.type}`}
                      className={cx("w-full text-left px-3 py-2 text-sm rounded-sm transition-colors", type === o.type ? "bg-gold text-navy" : "text-slate-300 hover:bg-white/5")}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Panel>

        {/* Item + config */}
        <Panel className="p-5 lg:col-span-2" data-testid="export-config">
          {!type ? (
            <div className="text-slate-500 text-sm py-16 text-center flex flex-col items-center gap-3"><Package size={28} className="text-slate-600" />Select an object on the left to begin.</div>
          ) : (
            <div className="space-y-6">
              {meta?.needs_id && (
                <div>
                  <SectionLabel className="mb-2">2 · Select the {meta.label.toLowerCase()}</SectionLabel>
                  {items === null ? <Loading /> : (
                    <>
                      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="export-item-search"
                        className="w-full bg-navy border border-white/10 px-3 py-2 text-sm text-white rounded-sm focus:border-gold focus:outline-none mb-2" />
                      <div className="max-h-52 overflow-y-auto border border-white/10 rounded-sm divide-y divide-white/5" data-testid="export-item-list">
                        {filteredItems.length === 0 ? <div className="text-slate-500 text-sm p-3">No items.</div> : filteredItems.map((x) => (
                          <button key={x.id} onClick={() => setObjId(x.id)} data-testid={`export-item-${x.id}`}
                            className={cx("w-full text-left px-3 py-2 text-sm transition-colors", objId === x.id ? "bg-gold/15 text-white" : "text-slate-300 hover:bg-white/5")}>
                            {itemLabel(type, x)}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              <div>
                <SectionLabel className="mb-2">{meta?.needs_id ? "3" : "2"} · Format</SectionLabel>
                <div className="flex flex-wrap gap-2" data-testid="export-formats">
                  {cat.formats.map((f) => {
                    const Icon = FMT_ICON[f] || FileDown;
                    return (
                      <button key={f} onClick={() => setFormat(f)} data-testid={`export-format-${f}`}
                        className={cx("flex items-center gap-1.5 px-3.5 py-2 text-sm rounded-sm border uppercase transition-colors", format === f ? "bg-gold text-navy border-gold" : "border-white/10 text-slate-300 hover:border-gold/50")}>
                        <Icon size={14} /> {f === "markdown" ? "MD" : f}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <SectionLabel className="mb-2">Report Profile</SectionLabel>
                  <select value={profile} onChange={(e) => setProfile(e.target.value)} data-testid="export-profile"
                    className="w-full bg-navy border border-white/10 px-3 py-2 text-sm text-white rounded-sm focus:border-gold focus:outline-none capitalize">
                    {cat.profiles.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <SectionLabel className="mb-2">Brand</SectionLabel>
                  <select value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="export-brand"
                    className="w-full bg-navy border border-white/10 px-3 py-2 text-sm text-white rounded-sm focus:border-gold focus:outline-none">
                    {cat.brands.map((b) => <option key={b.key} value={b.key}>{b.name}</option>)}
                  </select>
                </div>
              </div>

              <button onClick={doExport} disabled={!canExport || busy} data-testid="export-download-btn"
                className="w-full flex items-center justify-center gap-2 bg-gold text-navy px-5 py-3 text-sm font-semibold rounded-sm hover:bg-gold-light disabled:opacity-50 transition-colors">
                <Download size={16} /> {busy ? "Generating…" : `Export ${format.toUpperCase()}`}
              </button>
              {!canExport && meta?.needs_id && <p className="text-xs text-slate-500 text-center -mt-2">Select an item to enable export.</p>}
            </div>
          )}
        </Panel>
      </div>
    </StudioPage>
  );
}
